export const CUSTOM_FIELD_TYPES = [
  { value: "string", label: "String" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & Time" },
];

const NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

export const normalizeCustomFields = (value) =>
  asArray(value)
    .map((field, index) => {
      const type = CUSTOM_FIELD_TYPES.some((item) => item.value === field?.type)
        ? field.type
        : "string";

      return {
        uuid: field?.uuid || field?.id || `custom-field-${index}`,
        name: String(field?.name || "").trim(),
        caption: String(field?.caption || "").trim(),
        tooltip: String(field?.tooltip || "").trim(),
        type,
        required: Boolean(field?.required),
      };
    })
    .filter((field) => field.name || field.uuid);

export const getTemplateCustomFields = (template) =>
  normalizeCustomFields(
    template?.configuration?.customFields ??
      template?.meta?.customFields ??
      template?.customFields
  );

export const hasTemplateCustomFields = (template) =>
  getTemplateCustomFields(template).length > 0;

export const validateCustomFields = (fields) => {
  const normalized = normalizeCustomFields(fields);
  const names = new Set();
  const errors = {};

  normalized.forEach((field, index) => {
    const key = field.uuid || `custom-field-${index}`;
    const itemErrors = {};
    const nameKey = field.name.toLowerCase();

    if (!field.name) itemErrors.name = "Name is required";
    else if (!NAME_PATTERN.test(field.name)) {
      itemErrors.name = "Use an XML-safe name";
    } else if (names.has(nameKey)) {
      itemErrors.name = "Name must be unique";
    }

    if (field.name) names.add(nameKey);
    if (!CUSTOM_FIELD_TYPES.some((item) => item.value === field.type)) {
      itemErrors.type = "Type is required";
    }

    if (Object.keys(itemErrors).length) errors[key] = itemErrors;
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    fields: normalized,
  };
};

export const validateCustomValues = (fields, values = {}) => {
  const errors = {};

  normalizeCustomFields(fields).forEach((field) => {
    if (!field.required) return;

    const value = values[field.name];
    if (value === undefined || value === null || String(value).trim() === "") {
      errors[field.name] = "Required";
    }
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

const asFieldMap = (fields = []) =>
  normalizeCustomFields(fields).reduce((result, field) => {
    if (field.name) result[field.name] = field;
    return result;
  }, {});

const normalizeCustomValue = (value, field) => {
  if (typeof value !== "string") return value;

  const isDateTime = field?.type === "datetime" || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value);
  if (!isDateTime) return value;

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
  return value;
};

export const applyCustomValues = (json, values = {}, fields = []) => {
  const fieldMap = asFieldMap(fields);
  const custom = Object.keys(values || {}).reduce((result, key) => {
    const value = values[key];
    if (value !== undefined && value !== null && value !== "") {
      result[key] = normalizeCustomValue(value, fieldMap[key]);
    }
    return result;
  }, {});

  if (!Object.keys(custom).length) return json;

  return {
    ...(json || {}),
    custom: {
      ...((json?.custom && typeof json.custom === "object") ? json.custom : {}),
      ...custom,
    },
  };
};
